import { CATEGORIAS_GASTOS, GRUPOS_ORDEN } from './constantesGastos';

/**
 * Valida un monto de gasto fijo (input de tipo string desde un campo de texto).
 * @param {string} input - Valor ingresado por el usuario
 * @returns {{ valid: boolean, error?: string }} Resultado de validación con código de error si falla
 */
export function validarMontoGasto(input) {
  if (input === undefined || input === null || String(input).trim() === '') {
    return { valid: false, error: 'empty' };
  }

  const str = String(input).trim();
  const num = Number(str);

  if (isNaN(num)) {
    return { valid: false, error: 'not_numeric' };
  }

  if (num < 0) {
    return { valid: false, error: 'negative' };
  }

  // Check decimal places using string analysis
  if (str.includes('.')) {
    const decimals = str.split('.')[1];
    if (decimals && decimals.length > 2) {
      return { valid: false, error: 'too_many_decimals' };
    }
  }

  if (num > 999999999.99) {
    return { valid: false, error: 'exceeds_maximum' };
  }

  return { valid: true };
}

/**
 * Valida los campos de una entrada mensual.
 * @param {{ descripcion: string, monto: string|number, fecha?: string, mes: number, anio: number }} entrada
 * @returns {{ valid: boolean, errors?: { descripcion?: string, monto?: string, fecha?: string } }}
 */
export function validarEntrada({ descripcion, monto, fecha, mes, anio }) {
  const errors = {};

  // Validar descripción
  const descTrimmed = descripcion !== undefined && descripcion !== null
    ? String(descripcion).trim()
    : '';

  if (descTrimmed.length === 0) {
    errors.descripcion = 'La descripción es obligatoria';
  } else if (descTrimmed.length > 200) {
    errors.descripcion = 'Máximo 200 caracteres';
  }

  // Validar monto
  const montoStr = monto !== undefined && monto !== null ? String(monto).trim() : '';

  if (montoStr === '') {
    errors.monto = 'El monto debe ser numérico';
  } else {
    const montoNum = Number(montoStr);

    if (isNaN(montoNum)) {
      errors.monto = 'El monto debe ser numérico';
    } else if (montoNum <= 0) {
      errors.monto = 'El monto debe ser mayor a cero';
    } else if (montoNum > 999999999.99) {
      errors.monto = 'El monto excede el máximo permitido';
    } else if (montoStr.includes('.')) {
      const decimals = montoStr.split('.')[1];
      if (decimals && decimals.length > 2) {
        errors.monto = 'Máximo 2 decimales';
      }
    }
  }

  // Validar fecha (solo si se provee)
  if (fecha !== undefined && fecha !== null && fecha !== '') {
    const fechaDate = new Date(fecha + 'T00:00:00');
    if (isNaN(fechaDate.getTime())) {
      errors.fecha = 'La fecha debe estar dentro del mes seleccionado';
    } else {
      const day = fechaDate.getDate();
      const month = fechaDate.getMonth() + 1;
      const year = fechaDate.getFullYear();

      // Calcular el último día del mes
      const lastDay = new Date(anio, mes, 0).getDate();

      if (month !== mes || year !== anio || day < 1 || day > lastDay) {
        errors.fecha = 'La fecha debe estar dentro del mes seleccionado';
      }
    }
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }

  return { valid: true };
}

/**
 * Organiza un array de items de gastos en grupos según el orden fijo definido en GRUPOS_ORDEN.
 * Cada item debe tener al menos un campo `idCategoria`.
 * @param {Array<{ idCategoria: number, [key: string]: any }>} items
 * @returns {Object} Objeto con keys en el orden de GRUPOS_ORDEN, cada uno con su array de items
 */
export function organizarPorGrupo(items) {
  // Crear mapa de idCategoria → grupo
  const categoriaToGrupo = {};
  for (const grupo of GRUPOS_ORDEN) {
    for (const cat of CATEGORIAS_GASTOS[grupo]) {
      categoriaToGrupo[cat.id] = grupo;
    }
  }

  // Inicializar resultado con arrays vacíos en el orden correcto
  const resultado = {};
  for (const grupo of GRUPOS_ORDEN) {
    resultado[grupo] = [];
  }

  // Asignar cada item a su grupo
  for (const item of items) {
    const grupo = categoriaToGrupo[item.idCategoria];
    if (grupo) {
      resultado[grupo].push(item);
    }
  }

  return resultado;
}

/**
 * Calcula subtotales por grupo y total general a partir de un array de items.
 * Cada item debe tener al menos los campos `grupo` y `valor`.
 * @param {Array<{ grupo: string, valor: number }>} items
 * @returns {{ subtotales: { azcuenaga4b: number, azcuenaga2b: number, lobos: number, compartidos: number }, totalGeneral: number }}
 */
export function calcularSubtotales(items) {
  const subtotales = {};
  for (const grupo of GRUPOS_ORDEN) {
    subtotales[grupo] = 0;
  }

  for (const item of items) {
    if (subtotales[item.grupo] !== undefined) {
      subtotales[item.grupo] += item.valor;
    }
  }

  // Redondear subtotales para evitar errores de punto flotante
  for (const grupo of GRUPOS_ORDEN) {
    subtotales[grupo] = Math.round(subtotales[grupo] * 100) / 100;
  }

  const totalGeneral = Math.round(
    GRUPOS_ORDEN.reduce((sum, grupo) => sum + subtotales[grupo], 0) * 100
  ) / 100;

  return { subtotales, totalGeneral };
}
